import React, { useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BlogHero, ArticleList } from '../../components/Blog';
import { getArticlesBySilo } from '../../data/blog';
import { SILOS } from '../../data/blog/silos';
import type { SiloId } from '../../types/blog';
import { trackBlogSiloView } from '../../utils/analytics';

const VALID_SILOS = Object.keys(SILOS) as SiloId[];

const BlogSiloContent: React.FC<{ siloId: SiloId }> = ({ siloId }) => {
  const silo = SILOS[siloId];
  const articles = getArticlesBySilo(siloId);

  useEffect(() => {
    const pageTitle = `${silo.title} — Guides & conseils | SOS IT Services`;
    document.title = pageTitle;

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    const description = `${silo.description} — Guides pratiques et solutions de dépannage.`;
    metaDesc.setAttribute('content', description);

    const url = `https://sos-it-services.fr/blog/silo/${siloId}`;
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    const ogTags: Record<string, string> = {
      'og:title': pageTitle,
      'og:description': description,
      'og:url': url,
      'og:image': 'https://sos-it-services.fr/images/heroes/blog-hero.png',
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });

    window.scrollTo(0, 0);

    trackBlogSiloView(siloId);
  }, [siloId, silo]);

  return (
    <div className="pt-header md:pt-header-md min-h-screen bg-surface">
      <BlogHero
        title={`${silo.title} — Guides & conseils`}
        subtitle={silo.description}
        activeSilo={siloId}
      />

      <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      <section className="section-spacing bg-canvas border-t border-line">
        <div className="container-site">
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
          >
            <h2 className="text-xl md:text-2xl font-bold text-fg">
              {silo.title}{' '}
              {articles.length > 0 && (
                <span className="text-fg-muted font-normal text-base">({articles.length})</span>
              )}
            </h2>
            <p className="text-fg-muted text-sm mt-1">{silo.description}</p>
          </motion.div>

          <ArticleList articles={articles} perPage={9} />
        </div>
      </section>
    </div>
  );
};

const BlogSilo: React.FC = () => {
  const { siloId } = useParams<{ siloId: string }>();

  if (!siloId || !VALID_SILOS.includes(siloId as SiloId)) {
    return <Navigate to="/blog" replace />;
  }

  return <BlogSiloContent siloId={siloId as SiloId} />;
};

export default BlogSilo;
